/**
 * Flow **sources** — where a flow's data comes in.
 *
 * ## Why this exists
 *
 * Until now a flow had no notion of an input. Nodes were prompts, and prompts referenced each
 * other (`{{extract.result}}`), so the only data that ever entered a flow was the seed
 * (`{{prompt}}`) plus whatever a node happened to fetch for itself with its own tools. That is
 * why the graph of a news-driven flow showed no news: the feeds were hard-coded inside node
 * instructions, invisible to the graph, unversioned as data, and impossible to answer
 * "what will this flow actually be looking at?" about without reading every prompt.
 *
 * A source makes that declaration explicit and first-class:
 *
 * - it appears **on the graph**, upstream of the nodes that read it, so the picture shows where
 *   data enters instead of starting mid-air at the first prompt;
 * - it has a **declared shape**, so an author can see what a node will receive before running
 *   anything (see {@link SourceKindSpec.shape});
 * - it is referenced by a **stable token** — `{{sources.<sourceId>}}` — which the editor
 *   validates the same way it validates `{{node.result}}`.
 *
 * ## Storage
 *
 * Sources live in the flow definition under a top-level `sources` array. The server keeps the
 * definition as a `JsonObject` and patches it in place (`AiFlowStore.SaveVersionAsync`), so an
 * array it has never heard of survives a round-trip verbatim and needs no migration. Positions
 * for source nodes are stored in the same `layout` map as nodes, under the {@link sourceLayoutKey}
 * prefix, so they cannot collide with a nodeId.
 *
 * ## Runtime honesty
 *
 * {@link SourceKindSpec.runtime} records whether the executor can actually resolve a kind yet.
 * The editor surfaces that verbatim rather than implying every declaration is live: a source the
 * runtime cannot resolve interpolates to `[no value: sources.x]`, and an author deserves to know
 * that from the form rather than from a confidently wrong run.
 */

export type SourceKind =
  | "manual"
  | "document"
  | "collection"
  | "rss"
  | "http"
  | "files"
  | "watchlist"
  | "quotes"
  | "paper"
  | "runOutput"
  | "artifact"
  | "knowledge"
  | "directives"
  | "queue";

export interface SourceDefinition {
  /** Referenced from an instruction as `{{sources.<sourceId>}}`. Stable; renaming breaks refs. */
  sourceId: string;
  kind: SourceKind | string;
  name?: string;
  description?: string;
  /** Kind-specific settings — see {@link SOURCE_KINDS} for the fields each kind declares. */
  config?: Record<string, unknown>;
  /** nodeIds that read this source. Drives the edges drawn from the source into the graph. */
  consumers?: string[];
  // Forward-compat: preserve unknown fields verbatim.
  [extra: string]: unknown;
}

export interface SourceConfigField {
  key: string;
  label: string;
  /** `list` is entered as comma/newline-separated text and stored as a string[]. */
  control: "text" | "textarea" | "list";
  placeholder?: string;
  hint?: string;
  required?: boolean;
}

export interface SourceKindSpec {
  kind: SourceKind;
  label: string;
  /** One line: what this kind is, in the author's terms. */
  summary: string;
  fields: SourceConfigField[];
  /**
   * What a node receives when it interpolates this source. This is the flow's **data contract** —
   * the answer to "how do I know what the data will be" that does not require a run.
   */
  shape: string[];
  /**
   * `wired` — the executor resolves this kind and substitutes real content.
   * `declared` — authoring-only so far: it documents intent and draws on the graph, but at
   * runtime the reference interpolates to `[no value: …]`.
   */
  runtime: "wired" | "declared";
}

/**
 * The kinds an author can pick. Deliberately concrete: a generic "connector" field would push the
 * question of what the data looks like back onto the author, which is the thing this is meant to
 * answer.
 */
export const SOURCE_KINDS: SourceKindSpec[] = [
  {
    kind: "manual",
    label: "Literal value",
    summary:
      "Text you type here, versioned with the flow. Use it for the seed, a symbol list, a question, or a fixed instruction fragment shared by several nodes.",
    fields: [
      {
        key: "value",
        label: "Value",
        control: "textarea",
        placeholder: "USDILS, TEVA, ESLT",
        required: true,
      },
    ],
    shape: ["the text verbatim"],
    runtime: "wired",
  },
  {
    kind: "document",
    label: "KnowIt document",
    summary:
      "A knowledge-base document, by id. The node receives its current text, so editing the document changes what the flow reads without touching the flow.",
    fields: [
      {
        key: "documentId",
        label: "Document id",
        control: "text",
        placeholder: "efc3eb44-a414-4e9b-885f-5faa253f79b8",
        hint: "The id in the document URL.",
        required: true,
      },
    ],
    shape: ["title: string", "text: markdown", "updatedAt: ISO 8601"],
    runtime: "declared",
  },
  {
    kind: "collection",
    label: "KnowIt collection",
    summary:
      "Every document in a collection, as a list. Use it when the corpus grows and you do not want to re-author the flow each time.",
    fields: [
      {
        key: "collectionId",
        label: "Collection id",
        control: "text",
        required: true,
      },
      {
        key: "limit",
        label: "Max documents",
        control: "text",
        placeholder: "20",
        hint: "Guardrail: a collection that grows is a bill that grows.",
      },
    ],
    shape: ["documents[]: { id, title, text, updatedAt }"],
    runtime: "declared",
  },
  {
    kind: "rss",
    label: "RSS / Atom feeds",
    summary:
      "One or more feed URLs, fetched at run time. This is what news-driven flows were hiding inside their prompts.",
    fields: [
      {
        key: "urls",
        label: "Feed URLs",
        control: "list",
        placeholder:
          "https://feeds.reuters.com/reuters/topNews\nhttps://www.ft.com/rss/home",
        required: true,
      },
      {
        key: "windowHours",
        label: "Window (hours)",
        control: "text",
        placeholder: "6",
        hint: "Only items published inside the window are carried.",
      },
      {
        key: "maxItems",
        label: "Max items",
        control: "text",
        placeholder: "40",
      },
    ],
    shape: [
      "a JSON ARRAY of { title, link, publishedAt, summary, sourceName }",
      "publishedAt: ISO 8601, or \"\" when the feed gave no parseable date — empty, never invented",
      "sourceName: the feed's own title. Distinct values ARE the distinct wires (8 feeds carrying 1 wire is 1 outlet)",
      "newest first, filtered to the window, capped at max items",
    ],
    runtime: "wired",
  },
  {
    kind: "http",
    label: "HTTP endpoint",
    summary:
      "A call to a URL, carried as text or parsed JSON. The escape hatch for anything without a first-class kind — including KnowIt's own dot-RPC routes, which are POSTs with a JSON body.",
    fields: [
      { key: "url", label: "URL", control: "text", required: true },
      {
        key: "method",
        label: "Method",
        control: "text",
        placeholder: "GET | POST",
        hint: "KnowIt's own `pulse/*.*` routes are POST.",
      },
      {
        key: "body",
        label: "Request body (JSON)",
        control: "textarea",
        placeholder: '{"symbol":"TEVA"}',
        hint: "POST only.",
      },
      {
        key: "as",
        label: "Parse as",
        control: "text",
        placeholder: "json | text",
      },
      {
        key: "auth",
        label: "Credential",
        control: "text",
        placeholder: "knowit | none",
        hint: "Leave blank to infer from the URL: Docit's own servers get the signed-in workspace credential, every other host gets none. `knowit` demands it (and fails if the host is not a configured internal origin, rather than calling unauthenticated); `none` never sends it.",
      },
    ],
    shape: [
      "the response body: canonical JSON when `as=json`, otherwise text verbatim",
      "HTTP >= 400 fails the source with the status — it is never carried as if it were data",
      "`as=json` that does not parse is carried as text with a stated warning, not silently",
    ],
    runtime: "wired",
  },
  {
    kind: "files",
    label: "Workspace files",
    summary:
      "Files matched by a glob in the IDE workspace the flow runs in. For flows that read a repository rather than a knowledge base.",
    fields: [
      {
        key: "glob",
        label: "Glob",
        control: "text",
        placeholder: "documentation/ai-map/**/*.md",
        required: true,
      },
      {
        key: "maxFiles",
        label: "Max files",
        control: "text",
        placeholder: "25",
      },
    ],
    shape: ["files[]: { path, text }"],
    runtime: "declared",
  },
  {
    kind: "watchlist",
    label: "Pulse watchlist",
    summary:
      "A MarketPulse watchlist — its symbols and their current quotes — rather than a symbol list pasted into a prompt.",
    fields: [
      { key: "watchlistId", label: "Watchlist id", control: "text" },
      {
        key: "symbols",
        label: "Symbols (override)",
        control: "list",
        placeholder: "USDILS, TEVA, ESLT",
      },
    ],
    shape: ["symbols[]: string", "quotes[]: { symbol, price, asOf }"],
    runtime: "declared",
  },
  {
    kind: "paper",
    label: "Paper trading book",
    summary:
      "The team's MarketPulse paper (virtual) trading account: balances, open positions with marks and unrealized P&L, open orders and armed triggers. What a flow reads before deciding whether a trigger's intent should execute.",
    fields: [
      {
        key: "accountId",
        label: "Account id (required when the team has several)",
        control: "text",
      },
    ],
    shape: [
      "account: { id, name, baseCurrency }",
      "balances[]: { currency, amount }",
      "positions[]: { id, symbol, direction, quantity, avgEntryPrice, unrealizedPnl, status }",
      "openOrders[]: { id, symbol, side, orderType, quantity, status }",
      "armedTriggers[]: { id, name, kind, status }",
      "asOf: instant",
    ],
    runtime: "wired",
  },
  {
    kind: "quotes",
    label: "Quotes",
    summary:
      "Current prices for an explicit symbol list, from KnowIt's own server-side quote proxy (pulse/quotes.get) using your signed-in credential.",
    fields: [
      { key: "symbols", label: "Symbols", control: "list", required: true },
      {
        key: "url",
        label: "Endpoint (override)",
        control: "text",
        placeholder: "http://127.0.0.1:5010/api/pulse/quotes.get",
        hint: "Defaults to the configured Docit server. Only an internal origin receives the credential.",
      },
    ],
    shape: [
      "{ requested: string[], quotes: [{ symbol, price, changePct, asOf }] }",
      "`requested` is what the flow asked for — a symbol the provider did not return is simply absent from `quotes`, and this is how a consumer can tell",
      "no configured provider (available:false) and an empty quote list BOTH fail the source: neither is a price, and a node must not read them as one",
    ],
    runtime: "wired",
  },
  {
    kind: "runOutput",
    label: "Earlier run's output",
    summary:
      "The result of a named node from the previous run of this flow — the input a flow that watches something needs in order to say what changed.",
    fields: [
      { key: "nodeId", label: "Node id", control: "text", required: true },
      {
        key: "runs",
        label: "How many runs back",
        control: "text",
        placeholder: "1",
      },
    ],
    shape: [
      "the node's result text, from the run journal",
      "the journal keeps a COMPACTED copy (head + tail + routing lines) at the journal budget — when what comes back is compacted, the payload says so in a trailing [runOutput] note rather than looking complete",
    ],
    runtime: "wired",
  },
  // ── flow-workbench §2.1: the three kinds the executor grew since 0.5.45 ────
  // Shapes/fields mirror thinkinghub-vscode/src/flows/sourceFetchers.ts (the runtime).
  {
    kind: "artifact",
    label: "Another flow's artifact",
    summary:
      "Cross-flow handoff: the latest matching artifact another flow's runs recorded, fetched through the KnowIt mirror. The producing flow's read grant is enforced server-side and the egress is audited — a refusal degrades this source with the reason, never a silent blank.",
    fields: [
      {
        key: "flow",
        label: "Producing flow (slug)",
        control: "text",
        placeholder: "grove-pilot-daily",
        hint: "Name the flow, or pin a specific run below — one of the two is required.",
      },
      {
        key: "run",
        label: "Run id (override)",
        control: "text",
        hint: "Pins one executor run instead of 'latest run of the flow'.",
      },
      {
        key: "path",
        label: "Artifact path",
        control: "text",
        placeholder: "tree.json",
        required: true,
      },
      {
        key: "version",
        label: "Version (pin)",
        control: "text",
        placeholder: "latest",
      },
    ],
    shape: [
      "the artifact's content verbatim, in its recorded mediaType",
      "permission refusals (403) and missing artifacts (404) FAIL the source with the reason — degraded(), not empty",
      "content over the mirror's upload cap is recorded-but-refused: read it on the producing machine",
    ],
    runtime: "wired",
  },
  {
    kind: "knowledge",
    label: "Knowledge at a resolution",
    summary:
      "Asks the knowledge index (server-side, over projected layer artifacts) for the truths matching a topic at a chosen resolution — no paths, no flow internals. Producing flows' read grants apply; withheld matches are counted, not hidden.",
    fields: [
      {
        key: "topic",
        label: "Topic",
        control: "text",
        placeholder: "Kyiv strikes",
        required: true,
      },
      {
        key: "resolution",
        label: "Resolution",
        control: "text",
        placeholder: "L1 | L2 | L3",
        hint: "L1 ≤120 words per truth, L2 ≤40, L3 ≤15. Default L3.",
      },
      {
        key: "flow",
        label: "Producing flow (narrow)",
        control: "text",
      },
      {
        key: "limit",
        label: "Max truths",
        control: "text",
        placeholder: "5",
      },
    ],
    shape: [
      "{ topic, resolution, truths[], withheldByPermission } as JSON",
      "each truth carries its artifact pointer for anyone needing the level below",
      "no matching truths FAILS the source (with the withheld count when that is why)",
    ],
    runtime: "wired",
  },
  {
    kind: "directives",
    label: "Standing directives",
    summary:
      "The ACTIVE operator directives for a flow, rendered as a prompt-ready block. Zero directives resolves to an explicit \"none are in force\" statement — never an empty string — and a failed fetch degrades loudly instead of quietly meaning none.",
    fields: [
      {
        key: "flowId",
        label: "Flow (slug)",
        control: "text",
        placeholder: "this flow's slug",
        required: true,
        hint: "Usually this flow's own slug — directives are keyed by flow.",
      },
    ],
    shape: [
      "a markdown block: '## Standing directives for <flow>' + numbered instructions with who set each and when",
      "zero active directives: an explicit 'No standing directives are in force' block, never \"\"",
      "an unreachable server FAILS the source — 'no directives' is never inferred from an error",
    ],
    runtime: "wired",
  },
  {
    kind: "queue",
    label: "Message queue",
    summary:
      "Reads waiting messages from a RabbitMQ queue — the payloads that queue-transport nodes (and other knowit publishers) put there. The counterpart of a node's queue transport: what one flow publishes, another reads.",
    fields: [
      {
        key: "queue",
        label: "Queue name",
        control: "text",
        placeholder: "knowit.flow.news-analyst-v1.extract.out.v1",
        hint: "Queue-transport nodes publish to knowit.flow.{slug}.{nodeId}.out.v1 by default.",
        required: true,
      },
      {
        key: "max",
        label: "Max messages",
        control: "text",
        placeholder: "10",
        hint: "The most messages taken in one run. Default 10.",
      },
    ],
    shape: [
      "a JSON ARRAY of the waiting messages' payload JSONs, up to max messages",
      "each element is one message's payload verbatim — the JSON the publishing node's result carried",
      "an empty queue yields an empty array [], never a failure — no waiting work is a normal answer",
    ],
    runtime: "wired",
  },
];

export function sourceKindSpec(kind: string): SourceKindSpec | undefined {
  return SOURCE_KINDS.find((s) => s.kind === kind);
}

/** The token an instruction uses to read a source. */
export function sourceToken(sourceId: string): string {
  return `{{sources.${sourceId}}}`;
}

/** Layout key for a source node, namespaced so it cannot collide with a nodeId. */
export function sourceLayoutKey(sourceId: string): string {
  return `src:${sourceId}`;
}

/** True for a React Flow node id that represents a source rather than a flow node. */
export function isSourceNodeId(id: string): boolean {
  return id.startsWith("src:");
}

export function sourceIdFromNodeId(id: string): string {
  return id.startsWith("src:") ? id.slice(4) : id;
}

/**
 * Turn the author's comma/newline-separated text into the string[] the config stores. Both
 * separators are accepted because both are what people actually paste.
 */
export function parseList(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((v) => v.trim())
    .filter(Boolean);
}

export function formatList(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).join("\n");
  }
  return value === undefined || value === null ? "" : String(value);
}
